/**
 * Field Mapping Service - Handles field mapping configuration APIs
 * 
 * This service provides functions for fetching and managing field mapping configurations
 * from the ISE backend and JAMS.
 */

/** Resolved per-field source provenance (present only when juid is supplied and resolvable). */
export interface IResolvedFieldSource {
  sourceId: string
  sourceFeed: string
  sourceField: string
  sourcePath: string
}

// Response type for the ISE mapping-config/fields API
export interface IMappingFieldsResponse {
  success: boolean
  data: Array<{
    key: string
    sources: string[]
    recommendedSource?: string
    starterDefaultValue?: unknown
    /** Real mapped source(s) for the dealer's selected feed (manual-override staging). */
    resolvedSources?: IResolvedFieldSource[]
    /** True when no real provenance could be resolved; field must not be staged as an override. */
    provenanceMissing?: boolean
  }>
  juid: string
  configuredFeeds: string[]
  feedDetails: Array<{
    id: string
    friendlyName?: string
    type: string
  }>
}

/** Per-field provenance keyed by field name, for building manual override actions. */
export type FieldProvenanceMap = Record<
  string,
  { resolvedSources?: IResolvedFieldSource[]; provenanceMissing?: boolean }
>

/**
 * Builds a field -> provenance map from the mapping-config/fields response. Only carries the
 * provenance signals (resolvedSources / provenanceMissing); callers use it to decide whether a
 * feed-backed field edit can be staged as a manual override.
 */
export const extractFieldProvenanceMap = (resp: unknown): FieldProvenanceMap => {
  const data = (resp as IMappingFieldsResponse | null)?.data
  if (!Array.isArray(data)) return {}

  const map: FieldProvenanceMap = {}
  for (const entry of data) {
    const key = typeof entry?.key === 'string' ? entry.key : ''
    if (!key) continue
    map[key] = {
      resolvedSources: Array.isArray(entry.resolvedSources) ? entry.resolvedSources : undefined,
      provenanceMissing: entry.provenanceMissing === true,
    }
  }
  return map
}

/**
 * Fetches the field mapping configuration from JAMS
 * 
 * @param endpoint - JAMS backend endpoint
 * @param accountId - Dealer account ID (jazelId)
 * @param token - Authentication token
 * @returns Promise<IISEFieldMapGetConfigResponse | null> - Field mapping config or null if error
 */
export const fetchVehicleFieldMappingConfig = async (
  endpoint: string,
  accountId: string,
  token: string,
): Promise<IISEFieldMapGetConfigResponse | null> => {
  const productId = 'ise_srp_import_mapping'
  const path = `account/products/${accountId}/${productId}`
  const response = await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    console.error('Error fetching field feed options.')
    return null
  }

  return await response.json()
}

/**
 * Fetches available mapping fields from ISE backend
 * 
 * This endpoint returns all available fields with their possible sources
 * based on the dealer's configured feeds.
 * 
 * @param iseEndpoint - ISE backend endpoint
 * @param juid - Optional dealer account ID (jazelId)
 * @returns Promise<IMappingFieldsResponse | null> - Available fields with sources or null if error
 */
export const fetchIseMappingFields = async (
  iseEndpoint: string,
  juid?: string,
): Promise<IMappingFieldsResponse | null> => {
  const base = String(iseEndpoint).replace(/\/+$/g, '')
  const path = `mapping-config/fields`
  
  // Build URL with optional juid query parameter
  let url = `${base}/${path}`
  if (juid) {
    const params = new URLSearchParams({ juid })
    url = `${url}?${params.toString()}`
  }
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  try {
    const json = await response.json()
    if (!response.ok) return null
    return json
  } catch (e) {
    console.error('ise', 'fetchIseMappingFields', e)
    return null
  }
}

/** A single Publish Rule / Vehicle Grouping condition field from the backend catalog. */
export interface IRuleConditionField {
  /** Field key written into rule `field`. */
  key: string
  /** User-facing label shown in rule-condition dropdowns. */
  label: string
  /** True when provided by the system rather than configured in Field Mapping. */
  systemProvided: boolean
}

export interface IRuleConditionFieldsResponse {
  success: boolean
  data: IRuleConditionField[]
}

/**
 * Fetches the deliberate, server-owned catalog of Publish Rule / Vehicle
 * Grouping condition fields from the ISE backend.
 *
 * Unlike {@link fetchIseMappingFields}, this is intentionally
 * account-INDEPENDENT (no juid / dealer-feed filtering): rule conditions
 * evaluate vehicle values — including system-provided values like
 * `available_feeds` — so every account must see the same catalog. Rule field
 * availability must never be inferred from an account's saved rules.
 *
 * @param iseEndpoint - ISE backend endpoint
 * @returns Promise<IRuleConditionFieldsResponse | null> - Catalog or null on error
 */
export const fetchIseRuleConditionFields = async (
  iseEndpoint: string,
): Promise<IRuleConditionFieldsResponse | null> => {
  const base = String(iseEndpoint).replace(/\/+$/g, '')
  const url = `${base}/mapping-config/rule-fields`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  try {
    const json = await response.json()
    if (!response.ok) return null
    return json
  } catch (e) {
    console.error('ise', 'fetchIseRuleConditionFields', e)
    return null
  }
}

/**
 * Normalize a rule-condition-fields response into dropdown items.
 * Returns `{ title, value, systemProvided }` sorted by label.
 */
export const extractRuleConditionFieldOptions = (
  resp: unknown,
): Array<{ title: string; value: string; systemProvided: boolean }> => {
  const data = (resp as IRuleConditionFieldsResponse | null)?.data
  if (!Array.isArray(data)) return []
  return data
    .map((f) => ({
      title: typeof f?.label === 'string' && f.label.trim() ? f.label : String(f?.key ?? ''),
      value: String(f?.key ?? '').trim(),
      systemProvided: f?.systemProvided === true,
    }))
    .filter((o) => o.value.length > 0)
    .sort((a, b) => a.title.localeCompare(b.title))
}

/**
 * Normalize the `mapping-config/fields` response into a list of field keys.
 *
 * Historically this API has returned either:
 * - `{ data: string[] }`
 * - `{ data: Array<{ key: string; sources: string[] }> }`
 *
 * The Add/Edit/Details vehicle pages need only the field keys.
 */
export const extractIseMappingFieldKeys = (resp: unknown): string[] => {
  const data = (resp as any)?.data
  if (!Array.isArray(data) || data.length === 0) return []

  // Newer format: objects with { key, sources }
  if (typeof data[0] === 'object' && data[0] != null && 'key' in (data[0] as any)) {
    return (data as Array<any>)
      .map((x) => x?.key)
      .filter((x) => typeof x === 'string' && x.trim().length > 0)
  }

  // Older format: array of strings
  if (typeof data[0] === 'string') {
    return (data as Array<any>).filter((x) => typeof x === 'string' && x.trim().length > 0)
  }

  return []
}

export type FieldMappingDraftSelections = Record<string, string | string[]>

/**
 * Save Field Mapping as a ConfigDraft patch through the ISE backend.
 * This intentionally does not write directly to JAMS; Publish Changes promotes
 * the draft after preview/validation.
 */
export const saveFieldMappingDraft = async (
  iseEndpoint: string,
  jazelId: string,
  selections: FieldMappingDraftSelections,
  token: string,
  defaultValues?: Record<string, unknown>,
): Promise<boolean> => {
  const base = String(iseEndpoint || '').replace(/\/+$/g, '')
  const accountId = String(jazelId || '').trim()

  if (!base || !accountId || !token) return false

  const response = await fetch(`${base}/mapping-config/${encodeURIComponent(accountId)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      jazelId: accountId,
      selections,
      ...(defaultValues && Object.keys(defaultValues).length > 0 ? { defaultValues } : {}),
    }),
  })

  if (!response.ok) {
    console.error(`Failed to save field mapping draft: ${response.status}`)
    return false
  }

  return true
}

